"use client";

import VirtualPvEvent from "@/components/VirtualPvEvent";
import { Button } from "@/components/ui/Button";
import { cdnCampaignsPath } from "@/constants/cdnUrls";
import { DATA_LAYER_PAGE_NAMES } from "@/constants/dataLayer";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useLanguageAndCurrency } from "@/hooks/useLanguageAndCurrency";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { formatDate } from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sendGTMEvent } from "@next/third-parties/google";
import Image from "next/image";

function CampaignInfo({ campaign }) {
  const t = useTranslationDetector();
  const [copied, copy] = useCopyToClipboard();
  const { selectedCurrency } = useLanguageAndCurrency();
  // ProductType'a göre icon path'i döndür
  const getProductTypeIcon = () => {
    const iconMap = {
      Flight: "flight-tab-icon.svg",
      Bus: "bus-tab-icon.svg",
      Ferry: "ferry-tab-icon.svg",
      Hotel: "hotel-tab-icon.svg",
      Car: "car-tab-icon.svg",
    };
    const iconName = iconMap[campaign?.ProductType] || "flight-tab-icon.svg";
    return cdnCampaignsPath + iconName;
  };

  // Tarih formatlama
  const formatDateRange = (beginDate, endDate) => {
    const begin = beginDate
      ? formatDate(beginDate, {
          format: "DD MMMM YYYY",
        })
      : "";
    const end = endDate
      ? formatDate(endDate, {
          format: "DD MMMM YYYY",
        })
      : "";
    if (begin && end) return `${begin} - ${end}`;
    return begin || end || "";
  };

  let travelPeriod;

  if (campaign?.Details) {
    const [salesBegin, salesEnd] = Object.values(campaign?.Details);
    travelPeriod = formatDateRange(salesBegin, salesEnd);
  } else {
    travelPeriod = formatDateRange(campaign?.BeginDate, campaign?.EndDate);
  }

  const salesPeriod = formatDateRange(campaign?.BeginDate, campaign?.EndDate);

  const handleCopyCode = () => {
    if (campaign?.CampaignCode) {
      copy(String(campaign.CampaignCode));
      sendGTMEvent({
        event: "custom_event",
        event_category: "custom_event",
        event_action: "coupon_copy",
        creative_name: campaign?.Subtitle || campaign?.Title || "",
        promotion_id: campaign?.CampaignId,
        promotion_name: campaign?.TitleWithoutHtmlTags || campaign?.Title || "",
        coupon_name: campaign?.CampaignCode,
        coupon_value: null,
        coupon_currency: selectedCurrency?.toUpperCase() || "TRY",
        coupon_category: campaign?.ProductType,
      });
    }
  };

  return (
    <div className="shadow-small rounded-lg bg-white">
      <VirtualPvEvent pageType={DATA_LAYER_PAGE_NAMES.PROMOTIONS} />
      <div className="divide-border-weak flex flex-col divide-y md:flex-row md:divide-x md:divide-y-0">
        {/* Satış Dönemi */}
        {salesPeriod && (
          <div className="flex flex-1 items-center gap-4 p-6">
            <div className="flex !h-8 !w-8 flex-shrink-0 items-center justify-center">
              <Image
                src={cdnCampaignsPath + "calendar-blank.svg"}
                alt="Satış Dönemi"
                width={32}
                height={32}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <span className="text-foreground text-base leading-[21px] font-normal">
                {t("campaigns.salesPeriod") || "Satış Dönemi"}
              </span>
              <span className="text-foreground text-base leading-[21px] font-semibold">
                {salesPeriod}
              </span>
            </div>
          </div>
        )}

        {/* Seyahat Dönemi */}
        {travelPeriod && campaign?.ProductType && (
          <div className="flex flex-1 items-center gap-4 p-6">
            <div className="flex !h-8 !w-8 flex-shrink-0 items-center justify-center">
              <Image
                src={getProductTypeIcon()}
                alt="Seyahat Dönemi"
                width={32}
                height={32}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <span className="text-foreground text-base leading-[21px] font-normal">
                {t("campaigns.travelPeriod") || "Seyahat Dönemi"}
              </span>
              <span className="text-foreground text-base leading-[21px] font-semibold">
                {travelPeriod}
              </span>
            </div>
          </div>
        )}

        {/* Kampanya Kodu */}
        {campaign?.CampaignCode && (
          <div className="flex flex-1 items-center gap-4 p-6">
            <div className="flex !h-8 !w-8 flex-shrink-0 items-center justify-center">
              <FontAwesomeIcon
                icon={byPrefixAndName.far["tag"]}
                className="text-primary !h-8 !w-8"
              />
            </div>
            <div className="flex flex-1 items-center justify-between gap-[5px]">
              <div className="flex flex-col justify-between gap-2">
                <span className="text-foreground text-base leading-[21px] font-normal">
                  {t("campaigns.campaignCode")}
                </span>
                <span className="text-primary text-base leading-[21px] font-semibold">
                  {campaign?.CampaignCode || "Kampanya Kodu"}
                </span>
              </div>

              <Button
                size="sm"
                onClick={handleCopyCode}
                className="h-10 md:h-11"
              >
                <span>{t("campaigns.copy") || "Kopyala"}</span>
                <FontAwesomeIcon
                  icon={byPrefixAndName.far[copied ? "check" : "copy"]}
                  className="text-sm"
                />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignInfo;
